import React, { useEffect, useMemo, useState } from "react";
import {
    Alert,
    alpha,
    Box,
    Button,
    Chip,
    Container,
    Divider,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    Tab,
    Tabs,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    CircularProgress,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
    getTeacherDetails,
    logTeacherAttendance,
    removeTeachSubject,
    resetTeacherSalaryCycle,
    updateTeacher,
} from "../../../redux/teacherRelated/teacherHandle";
import { clearTeacherFeedback } from "../../../redux/teacherRelated/teacherSlice";
import Popup from "../../../components/Popup";
import { PASSWORD_MIN_HELPER, validatePasswordIfProvided } from "../../../utils/passwordValidation";
import ConfirmDeleteDialog from "../../../components/ConfirmDeleteDialog";
import { formatSalaryPeriodLabel, currentSalaryPeriod } from "../../../utils/salaryPeriod";
import TeacherHrPanel from "./TeacherHrPanel";

const ATTENDANCE_STATUS = ["Present", "Absent", "Late", "Leave", "Half-day", "Other"];

function TabPanel({ children, value, index }) {
    if (value !== index) return null;
    return <Box sx={{ pt: 2 }}>{children}</Box>;
}

function ReadOnlyField({ label, value, xs = 12, sm = 6 }) {
    const display = value === null || value === undefined || value === "" ? "—" : value;
    return (
        <Grid item xs={xs} sm={sm}>
            <Typography variant="caption" color="text.secondary" fontWeight={600} display="block">
                {label}
            </Typography>
            <Typography variant="body1" sx={{ wordBreak: "break-word", mt: 0.25 }}>
                {display}
            </Typography>
        </Grid>
    );
}

function formatDateLabel(value) {
    if (!value) return "—";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleDateString();
}

const TeacherDetails = () => {
    const navigate = useNavigate();
    const params = useParams();
    const dispatch = useDispatch();
    const { loading, teacherDetails, error, response } = useSelector((state) => state.teacher);
    const { currentUser } = useSelector((state) => state.user);

    const teacherID = params.id;
    const [tab, setTab] = useState(0);
    const [showPopup, setShowPopup] = useState(false);
    const [message, setMessage] = useState("");
    const [deleteDialog, setDeleteDialog] = useState(null);

    const [password, setPassword] = useState("");
    const [salaryAmount, setSalaryAmount] = useState("");
    const [payrollRef, setPayrollRef] = useState("");
    const [annualLeaveAllocated, setAnnualLeaveAllocated] = useState("");
    const [annualLeaveUsed, setAnnualLeaveUsed] = useState("");
    const [yearsExperience, setYearsExperience] = useState("");
    const [employmentDate, setEmploymentDate] = useState("");
    const [contractStartDate, setContractStartDate] = useState("");
    const [contractEndDate, setContractEndDate] = useState("");

    const [attDate, setAttDate] = useState(() => new Date().toISOString().slice(0, 10));
    const [attStatus, setAttStatus] = useState("Present");
    const [presentCount, setPresentCount] = useState("");
    const [absentCount, setAbsentCount] = useState("");
    const [savingProfile, setSavingProfile] = useState(false);
    const [savingAttendance, setSavingAttendance] = useState(false);
    const [resettingSalary, setResettingSalary] = useState(false);

    useEffect(() => {
        if (!teacherID) return;
        dispatch(clearTeacherFeedback());
        dispatch(getTeacherDetails(teacherID));
    }, [dispatch, teacherID]);

    const detail = useMemo(() => {
        if (!teacherDetails || typeof teacherDetails !== "object") return null;
        const id = teacherDetails._id || teacherDetails.id;
        if (!id || String(id) !== String(teacherID)) return null;
        return teacherDetails;
    }, [teacherDetails, teacherID]);

    useEffect(() => {
        if (!detail) return;
        setPassword("");
        setSalaryAmount(
            detail.salaryAmount !== null && detail.salaryAmount !== undefined && detail.salaryAmount !== ""
                ? String(detail.salaryAmount)
                : ""
        );
        setPayrollRef(detail.payrollRef || "");
        setAnnualLeaveAllocated(
            detail.annualLeaveAllocated !== null && detail.annualLeaveAllocated !== undefined
                ? String(detail.annualLeaveAllocated)
                : ""
        );
        setAnnualLeaveUsed(
            detail.annualLeaveUsed !== null && detail.annualLeaveUsed !== undefined
                ? String(detail.annualLeaveUsed)
                : "0"
        );
        setYearsExperience(
            detail.yearsExperience !== null && detail.yearsExperience !== undefined
                ? String(detail.yearsExperience)
                : ""
        );
        setEmploymentDate(detail.employmentDate ? new Date(detail.employmentDate).toISOString().slice(0, 10) : "");
        setContractStartDate(detail.contractStartDate ? new Date(detail.contractStartDate).toISOString().slice(0, 10) : "");
        setContractEndDate(detail.contractEndDate ? new Date(detail.contractEndDate).toISOString().slice(0, 10) : "");
    }, [detail]);

    useEffect(() => {
        if (response) {
            setMessage(response);
            setShowPopup(true);
        }
    }, [response]);

    const attendanceRows = useMemo(() => {
        if (!detail?.attendance || !Array.isArray(detail.attendance)) return [];
        return [...detail.attendance].sort((a, b) => {
            const da = new Date(a.date).getTime();
            const db = new Date(b.date).getTime();
            return db - da;
        });
    }, [detail]);

    const assignedSubjects = useMemo(() => {
        if (!detail) return [];
        if (Array.isArray(detail.teachSubjects) && detail.teachSubjects.length > 0) {
            return detail.teachSubjects;
        }
        if (detail.teachSubject?.subName) return [detail.teachSubject];
        return [];
    }, [detail]);

    const isSubjectNamePresent = assignedSubjects.length > 0;

    const handleAddSubject = () => {
        if (!detail?._id) return;
        navigate(`/Admin/teachers/chooseclass/${detail._id}`);
    };

    const handleSaveProfile = async (e) => {
        e.preventDefault();
        if (!detail?._id) return;
        setSavingProfile(true);
        const payload = {
            payrollRef: payrollRef.trim() || null,
            annualLeaveAllocated: annualLeaveAllocated.trim() === "" ? null : annualLeaveAllocated.trim(),
            annualLeaveUsed: annualLeaveUsed.trim() === "" ? "0" : annualLeaveUsed.trim(),
            yearsExperience: yearsExperience.trim() === "" ? null : yearsExperience.trim(),
            employmentDate: employmentDate || null,
            contractStartDate: contractStartDate || null,
            contractEndDate: contractEndDate || null,
        };
        if (salaryAmount.trim() === "") payload.salaryAmount = null;
        else payload.salaryAmount = salaryAmount.trim();
        if (password.trim()) {
            const passwordErr = validatePasswordIfProvided(password);
            if (passwordErr) {
                setMessage(passwordErr);
                setShowPopup(true);
                setSavingProfile(false);
                return;
            }
            payload.password = password.trim();
        }

        await dispatch(updateTeacher(detail._id, payload));
        setSavingProfile(false);
        setPassword("");
    };

    const handleLogAttendance = async (e) => {
        e.preventDefault();
        if (!detail?._id) return;
        setSavingAttendance(true);
        const iso = attDate ? new Date(attDate + "T12:00:00").toISOString() : new Date().toISOString();
        await dispatch(
            logTeacherAttendance(detail._id, {
                date: iso,
                status: attStatus,
                presentCount: presentCount.trim() || undefined,
                absentCount: absentCount.trim() || undefined,
            })
        );
        setSavingAttendance(false);
        setPresentCount("");
        setAbsentCount("");
    };

    const leaveRemaining =
        detail?.annualLeaveAllocated != null && detail?.annualLeaveUsed != null
            ? Number(detail.annualLeaveAllocated) - Number(detail.annualLeaveUsed)
            : null;

    if (loading && !detail) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!loading && !detail && (response || error)) {
        return (
            <Container sx={{ py: 4 }}>
                <Alert severity="error" sx={{ borderRadius: 2 }}>
                    {response || error?.message || "Could not load this teacher."}
                </Alert>
                <Button sx={{ mt: 2 }} onClick={() => navigate("/Admin/teachers")}>
                    Back to teachers
                </Button>
            </Container>
        );
    }

    if (!detail) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box
            sx={{
                minHeight: "100%",
                py: { xs: 2, sm: 3 },
                background: (t) =>
                    t.palette.mode === "light"
                        ? `linear-gradient(180deg, ${alpha(t.palette.primary.main, 0.04)} 0%, ${t.palette.grey[50]} 32%)`
                        : alpha(t.palette.common.black, 0.12),
            }}
        >
            <Container maxWidth="lg">
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }} flexWrap="wrap" gap={2}>
                    <Box>
                        <Typography variant="overline" color="text.secondary">
                            Teacher profile
                        </Typography>
                        <Typography variant="h4" fontWeight={700}>
                            {detail.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {isSubjectNamePresent
                                ? assignedSubjects
                                      .map((s) => `${s.subName}${s.sclassName?.sclassName ? ` (${s.sclassName.sclassName})` : ""}`)
                                      .join(" · ")
                                : "No subjects assigned"}
                        </Typography>
                        <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
                            <Chip
                                size="small"
                                label={detail.profileCompleted ? "Profile complete" : "Profile incomplete"}
                                color={detail.profileCompleted ? "success" : "warning"}
                                variant="outlined"
                            />
                            <Chip
                                size="small"
                                label={`TSC: ${detail.tscVerificationStatus || "Pending"}`}
                                color={
                                    detail.tscVerificationStatus === "Verified"
                                        ? "success"
                                        : detail.tscVerificationStatus === "Rejected"
                                          ? "error"
                                          : "warning"
                                }
                                variant="outlined"
                            />
                            {detail.yearsExperience != null && detail.yearsExperience !== "" ? (
                                <Chip size="small" label={`${detail.yearsExperience} yrs experience`} variant="outlined" />
                            ) : null}
                        </Stack>
                    </Box>
                    <Stack direction="row" spacing={1}>
                        <Button variant="outlined" onClick={() => navigate("/Admin/teachers")}>
                            Back
                        </Button>
                        <Button variant="contained" onClick={handleAddSubject}>
                            {isSubjectNamePresent ? "Assign another subject" : "Assign subject"}
                        </Button>
                    </Stack>
                </Stack>

                <Paper
                    elevation={0}
                    sx={{
                        borderRadius: 3,
                        border: (t) => `1px solid ${alpha(t.palette.divider, 0.9)}`,
                        overflow: "hidden",
                    }}
                >
                    <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ px: 2, pt: 1 }} variant="scrollable" scrollButtons="auto">
                        <Tab label="Personal" />
                        <Tab label="Academic" />
                        <Tab label="Banking" />
                        <Tab label="HR & payroll" />
                        <Tab label="HR dossier" />
                        <Tab label="Attendance log" />
                    </Tabs>
                    <Divider />

                    <Box sx={{ px: { xs: 2, sm: 3 }, pb: 3 }}>
                        <TabPanel value={tab} index={0}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Personal details submitted by the teacher (read-only).
                            </Typography>
                            <Grid container spacing={2}>
                                <ReadOnlyField label="Full name" value={detail.name} />
                                <ReadOnlyField
                                    label="Email"
                                    value={
                                        detail.emailIsTemporary
                                            ? `${detail.email} (temporary — teacher must update)`
                                            : detail.email
                                    }
                                />
                                <ReadOnlyField label="Phone" value={detail.phone} />
                                <ReadOnlyField label="Date of birth" value={formatDateLabel(detail.dateOfBirth)} />
                                <ReadOnlyField label="Gender" value={detail.gender} />
                                <ReadOnlyField label="National ID / passport" value={detail.nationalId} />
                                <ReadOnlyField label="Physical address" value={detail.physicalAddress} xs={12} sm={12} />
                                <ReadOnlyField label="Emergency contact name" value={detail.emergencyContactName} />
                                <ReadOnlyField label="Emergency contact phone" value={detail.emergencyContactPhone} />
                            </Grid>
                        </TabPanel>

                        <TabPanel value={tab} index={1}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Academic and assignment details (read-only).
                            </Typography>
                            <Grid container spacing={2} sx={{ mb: 2 }}>
                                {assignedSubjects.length === 0 ? (
                                    <Grid item xs={12}>
                                        <Typography variant="body2" color="text.secondary">
                                            No subjects assigned yet.
                                        </Typography>
                                    </Grid>
                                ) : (
                                    assignedSubjects.map((subject) => (
                                        <Grid item xs={12} key={subject._id || subject.id}>
                                            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                                                <Stack
                                                    direction={{ xs: "column", sm: "row" }}
                                                    justifyContent="space-between"
                                                    alignItems={{ sm: "center" }}
                                                    spacing={1}
                                                >
                                                    <Box>
                                                        <Typography fontWeight={700}>{subject.subName}</Typography>
                                                        <Typography variant="body2" color="text.secondary">
                                                            {subject.sclassName?.sclassName || "—"} ·{" "}
                                                            {subject.sessions ? `${subject.sessions} periods/week` : "—"}
                                                        </Typography>
                                                    </Box>
                                                    <Button
                                                        size="small"
                                                        color="error"
                                                        variant="outlined"
                                                        onClick={() =>
                                                            setDeleteDialog({
                                                                title: `Remove subject: ${subject.subName}`,
                                                                description:
                                                                    "This will remove this subject assignment from the teacher. The subject itself is not deleted.",
                                                                confirmWord: subject.subName,
                                                                onConfirm: async () => {
                                                                    const sid = subject._id || subject.id;
                                                                    await dispatch(removeTeachSubject(detail._id, sid));
                                                                    dispatch(getTeacherDetails(teacherID));
                                                                },
                                                            })
                                                        }
                                                    >
                                                        Remove assignment
                                                    </Button>
                                                </Stack>
                                            </Paper>
                                        </Grid>
                                    ))
                                )}
                            </Grid>
                            <Grid container spacing={2}>
                                <ReadOnlyField label="TCM number" value={detail.tscNumber} />
                                <ReadOnlyField label="Contract type" value={detail.contractType} />
                                <ReadOnlyField label="Qualifications" value={detail.qualifications} xs={12} sm={12} />
                            </Grid>
                        </TabPanel>

                        <TabPanel value={tab} index={2}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Banking details for payroll (read-only).
                            </Typography>
                            <Grid container spacing={2}>
                                <ReadOnlyField label="Bank name" value={detail.bankName} />
                                <ReadOnlyField label="Branch" value={detail.bankBranch} />
                                <ReadOnlyField label="Account number" value={detail.accountNumber} />
                                <ReadOnlyField label="Account holder name" value={detail.accountHolderName} />
                            </Grid>
                        </TabPanel>

                        <TabPanel value={tab} index={3}>
                            <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2, bgcolor: "action.hover" }}>
                                <Typography variant="subtitle2" gutterBottom>
                                    TSC & employment
                                </Typography>
                                <Grid container spacing={2} sx={{ mb: 1 }}>
                                    <ReadOnlyField label="TCM / TSC number" value={detail.tscNumber} />
                                    <ReadOnlyField label="TSC verification" value={detail.tscVerificationStatus || "Pending"} />
                                    <ReadOnlyField label="Contract type" value={detail.contractType} />
                                    <ReadOnlyField label="Qualifications (summary)" value={detail.qualifications} xs={12} sm={12} />
                                </Grid>
                            </Paper>

                            <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2, bgcolor: "action.hover" }}>
                                <Typography variant="subtitle2" gutterBottom>
                                    Leave management
                                </Typography>
                                <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }}>
                                    <Typography variant="body2">
                                        Allocated: <strong>{detail.annualLeaveAllocated ?? "—"}</strong> days · Used:{" "}
                                        <strong>{detail.annualLeaveUsed ?? 0}</strong>
                                    </Typography>
                                    {leaveRemaining !== null && !Number.isNaN(leaveRemaining) && (
                                        <Chip size="small" label={`Remaining (approx.): ${leaveRemaining}`} color="primary" variant="outlined" />
                                    )}
                                </Stack>
                            </Paper>

                            <Box component="form" onSubmit={handleSaveProfile}>
                                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                    Administrator controls
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    Set payroll and leave balances. Personal, academic, and banking fields are completed by the teacher.
                                </Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            type="password"
                                            label="Reset password (optional)"
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            autoComplete="new-password"
                                            helperText={`Leave blank to keep current. ${PASSWORD_MIN_HELPER} if resetting.`}
                                            inputProps={{ minLength: 8 }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Salary amount"
                                            type="number"
                                            inputProps={{ min: 0, step: "0.01" }}
                                            value={salaryAmount}
                                            onChange={(e) => setSalaryAmount(e.target.value)}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1}>
                                            <Typography variant="body2" color="text.secondary">
                                                {formatSalaryPeriodLabel(
                                                    detail?.salaryCurrentPeriod || currentSalaryPeriod()
                                                )}{" "}
                                                salary:
                                            </Typography>
                                            <Chip
                                                size="small"
                                                label={
                                                    detail?.salaryPaymentStatus === "Paid"
                                                        ? "Paid this month"
                                                        : detail?.salaryPaymentStatus === "Pending"
                                                          ? "Pending approval"
                                                          : "Unpaid this month"
                                                }
                                                color={
                                                    detail?.salaryPaymentStatus === "Paid"
                                                        ? "success"
                                                        : detail?.salaryPaymentStatus === "Pending"
                                                          ? "warning"
                                                          : "default"
                                                }
                                            />
                                            {detail?.salaryLastPaidPeriod ? (
                                                <Typography variant="caption" color="text.secondary">
                                                    Last paid month:{" "}
                                                    {detail.salaryLastPaidPeriodLabel ||
                                                        formatSalaryPeriodLabel(detail.salaryLastPaidPeriod)}
                                                </Typography>
                                            ) : null}
                                            {detail?.salaryPaymentStatus === "Paid" ? (
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    disabled={resettingSalary}
                                                    onClick={async () => {
                                                        if (
                                                            !window.confirm(
                                                                "Start a new salary cycle? The teacher will show as unpaid until the next salary expense is approved."
                                                            )
                                                        ) {
                                                            return;
                                                        }
                                                        setResettingSalary(true);
                                                        await dispatch(resetTeacherSalaryCycle(teacherID));
                                                        setResettingSalary(false);
                                                        dispatch(getTeacherDetails(teacherID));
                                                    }}
                                                >
                                                    Reset salary cycle
                                                </Button>
                                            ) : null}
                                        </Stack>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Payroll reference"
                                            value={payrollRef}
                                            onChange={(e) => setPayrollRef(e.target.value)}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Annual leave allocated"
                                            type="number"
                                            inputProps={{ min: 0 }}
                                            value={annualLeaveAllocated}
                                            onChange={(e) => setAnnualLeaveAllocated(e.target.value)}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Annual leave used"
                                            type="number"
                                            inputProps={{ min: 0 }}
                                            value={annualLeaveUsed}
                                            onChange={(e) => setAnnualLeaveUsed(e.target.value)}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Years of experience"
                                            type="number"
                                            inputProps={{ min: 0 }}
                                            value={yearsExperience}
                                            onChange={(e) => setYearsExperience(e.target.value)}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <TextField
                                            fullWidth
                                            label="Employment date"
                                            type="date"
                                            InputLabelProps={{ shrink: true }}
                                            value={employmentDate}
                                            onChange={(e) => setEmploymentDate(e.target.value)}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <TextField
                                            fullWidth
                                            label="Contract start"
                                            type="date"
                                            InputLabelProps={{ shrink: true }}
                                            value={contractStartDate}
                                            onChange={(e) => setContractStartDate(e.target.value)}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <TextField
                                            fullWidth
                                            label="Contract end"
                                            type="date"
                                            InputLabelProps={{ shrink: true }}
                                            value={contractEndDate}
                                            onChange={(e) => setContractEndDate(e.target.value)}
                                        />
                                    </Grid>
                                </Grid>

                                <Stack direction="row" justifyContent="flex-end" sx={{ mt: 3 }}>
                                    <Button type="submit" variant="contained" disabled={savingProfile}>
                                        {savingProfile ? <CircularProgress size={22} /> : "Save HR settings"}
                                    </Button>
                                </Stack>
                            </Box>
                        </TabPanel>

                        <TabPanel value={tab} index={4}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Full HR dossier: qualifications verification, deployment history, CPD, appraisals, payroll
                                structure, and payslips. Use the TSC verify buttons after checking the TCM number against TSC
                                records.
                            </Typography>
                            <TeacherHrPanel
                                teacherId={teacherID}
                                schoolId={currentUser?.school?._id || currentUser?.school?.id || currentUser?.schoolId}
                                adminId={currentUser?._id || currentUser?.id}
                                onTscUpdated={() => dispatch(getTeacherDetails(teacherID))}
                            />
                        </TabPanel>

                        <TabPanel value={tab} index={5}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Daily attendance records. Submitting the same calendar day updates the existing row.
                            </Typography>

                            <Box component="form" onSubmit={handleLogAttendance} sx={{ mb: 3 }}>
                                <Grid container spacing={2} alignItems="flex-end">
                                    <Grid item xs={12} sm={3}>
                                        <TextField
                                            fullWidth
                                            type="date"
                                            label="Date"
                                            InputLabelProps={{ shrink: true }}
                                            value={attDate}
                                            onChange={(e) => setAttDate(e.target.value)}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={3}>
                                        <FormControl fullWidth>
                                            <InputLabel>Status</InputLabel>
                                            <Select label="Status" value={attStatus} onChange={(e) => setAttStatus(e.target.value)}>
                                                {ATTENDANCE_STATUS.map((s) => (
                                                    <MenuItem key={s} value={s}>
                                                        {s}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} sm={2}>
                                        <TextField
                                            fullWidth
                                            label="Present count"
                                            value={presentCount}
                                            onChange={(e) => setPresentCount(e.target.value)}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={2}>
                                        <TextField
                                            fullWidth
                                            label="Absent count"
                                            value={absentCount}
                                            onChange={(e) => setAbsentCount(e.target.value)}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={2}>
                                        <Button fullWidth variant="contained" type="submit" disabled={savingAttendance}>
                                            {savingAttendance ? <CircularProgress size={22} /> : "Log"}
                                        </Button>
                                    </Grid>
                                </Grid>
                            </Box>

                            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Date</TableCell>
                                            <TableCell>Status</TableCell>
                                            <TableCell align="right">Present</TableCell>
                                            <TableCell align="right">Absent</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {attendanceRows.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={4}>
                                                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                                                        No attendance rows yet.
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            attendanceRows.map((row) => {
                                                const d = row.date ? new Date(row.date) : null;
                                                const label = d && !Number.isNaN(d.getTime()) ? d.toLocaleDateString() : "—";
                                                return (
                                                    <TableRow key={row._id || row.id}>
                                                        <TableCell>{label}</TableCell>
                                                        <TableCell>{row.status || "—"}</TableCell>
                                                        <TableCell align="right">{row.presentCount ?? "—"}</TableCell>
                                                        <TableCell align="right">{row.absentCount ?? "—"}</TableCell>
                                                    </TableRow>
                                                );
                                            })
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </TabPanel>
                    </Box>
                </Paper>
            </Container>
            <Popup message={message} setShowPopup={setShowPopup} showPopup={showPopup} />
            <ConfirmDeleteDialog
                open={Boolean(deleteDialog)}
                onClose={() => setDeleteDialog(null)}
                onConfirm={async () => {
                    if (deleteDialog?.onConfirm) {
                        await deleteDialog.onConfirm();
                    }
                    setDeleteDialog(null);
                }}
                title={deleteDialog?.title ?? ""}
                description={deleteDialog?.description ?? ""}
                confirmWord={deleteDialog?.confirmWord ?? ""}
                confirmLabel="Remove assignment"
            />
        </Box>
    );
};

export default TeacherDetails;
